// Carryable items: ingredients, cups, kettles, blender jugs, extinguishers.
import { Node } from '../engine/renderer.js';
import { col } from '../engine/math.js';
import { INGREDIENTS, RECIPES, SLUDGE, matchRecipe, reachableRecipes } from './data.js';
import * as M from './models.js';

let UID = 1;

/** Would handing this item across the bar complete an order for this recipe? */
export function servable(it, r) {
  if (it.kind === 'cup') return it.drink === r.id && !!it.fizzy === !!r.fizz;
  if (it.kind === 'bobacup') return it.drink === r.id && it.complete;
  return false;
}

export class Item {
  constructor(kind) {
    this.kind = kind;
    this.uid = UID++;
    this.node = new Node();
    this.holder = null; // station | chef | 'floor' | 'air'
    this.bob = Math.random() * 6;
  }
  get throwable() {
    return false;
  }
  get glow() {
    return 0;
  }
  get glowColor() {
    return [1, 1, 1];
  }
  dispose() {
    if (this.node.parent) this.node.parent.remove(this.node);
  }
  update() {}
}

export class Ingredient extends Item {
  constructor(id) {
    super('ing');
    this.id = id;
    this.def = INGREDIENTS[id];
    this.state = 'raw';
    this.prog = 0;
    this.meshNode = new Node(M.ingredientMesh(id, 'raw'));
    this.meshNode.scale = [1.3, 1.3, 1.3];
    if (id === 'banana') this.meshNode.rotation = [0, 0.55, 0];
    this.node.add(this.meshNode);
  }
  get throwable() {
    return true;
  }
  get needsPrep() {
    return this.def.prep && this.state === 'raw';
  }
  get ready() {
    return !this.needsPrep;
  }
  prepare() {
    this.state = this.def.prep === 'chop' ? 'chopped' : 'ground';
    this.meshNode.mesh = M.ingredientMesh(this.id, this.state);
    this.prog = 0;
  }
  get glow() {
    return this.def.glow || 0;
  }
  get glowColor() {
    return col(this.def.color);
  }
  get iconKey() {
    return this.id + ':' + this.state;
  }
}

export class Cup extends Item {
  constructor() {
    super('cup');
    this.dirty = false;
    this.count = 1; // stacked dirty cups
    this.drink = null;
    this.fizzy = false;
    this.fizzProg = 0;
    this.cupNodes = [];
    this.liquid = new Node(M.liquidMesh(), { castShadow: false });
    this.liquid.position = [0, 0.13, 0];
    this.liquid.visible = false;
    this.body = new Node();
    this.body.scale = [1.2, 1.2, 1.2];
    this.node.add(this.body);
    this.body.add(this.liquid);
    this._rebuild();
  }
  _rebuild() {
    for (const n of this.cupNodes) this.body.remove(n);
    this.cupNodes = [];
    for (let i = 0; i < this.count; i++) {
      const n = new Node(M.cupMesh(this.dirty));
      n.position = [0, i * 0.075, 0];
      this.body.add(n);
      this.cupNodes.push(n);
    }
  }
  setDirty(d) {
    this.dirty = d;
    this.drink = null;
    this.fizzy = false;
    this.liquid.visible = false;
    this._rebuild();
  }
  addStack(other) {
    this.count += other.count;
    this._rebuild();
  }
  fill(drinkId) {
    this.drink = drinkId;
    this.fizzy = false;
    this.fizzProg = 0;
    this._dress();
  }
  setFizzy(v) {
    this.fizzy = v;
    this._dress();
  }
  _dress() {
    this.liquid.visible = !!this.drink;
    if (!this.drink) return;
    this.liquid.mesh = M.drinkMesh(this.drink, this.fizzy);
    this.liquid.position = [0, 0, 0];
    this.liquid.tint = [1, 1, 1, 1];
    this.liquid.emissive = [0, 0, 0];
  }
  empty() {
    this.drink = null;
    this.fizzy = false;
    this.liquid.visible = false;
  }
  get isCleanEmpty() {
    return !this.dirty && !this.drink;
  }
  get glow() {
    return this.drink && RECIPES[this.drink] ? RECIPES[this.drink].glow || 0 : 0;
  }
  get glowColor() {
    return this.drink ? col((RECIPES[this.drink] || SLUDGE).color) : [1, 1, 1];
  }
}

/**
 * Disposable boba cup. Built up in steps: pearls and tea (either order), then sealed in the
 * Sealer, then a straw is stabbed through the seal. Guests take it with them when they leave.
 */
export class BobaCup extends Item {
  constructor() {
    super('bobacup');
    this.pearls = false;
    this.drink = null;
    this.sealed = false;
    this.straw = false;
    this.sealProg = 0;
    this.node.add(new Node(M.bobaCupMesh()));
    const part = (mesh) => {
      const n = new Node(mesh, { castShadow: false });
      n.visible = false;
      this.node.add(n);
      return n;
    };
    this.pearlNode = part(M.bobaPearlsMesh());
    this.liquid = part(M.bobaLiquidMesh());
    this.lid = part(M.bobaSealMesh());
    this.strawNode = part(M.bobaStrawMesh());
    // clear plastic: drawn translucent, last, so everything inside shows through
    this.shell = new Node(M.bobaShellMesh(), { castShadow: false });
    this.shell.tint = [1, 1, 1, 0.3];
    this.node.add(this.shell);
  }
  get canTakePearls() {
    return !this.pearls && !this.sealed;
  }
  get canPour() {
    return !this.drink && !this.sealed;
  }
  /** Pearls and tea are in: ready for the Sealer. */
  get sealable() {
    return this.pearls && !!this.drink && !this.sealed;
  }
  get complete() {
    return this.pearls && !!this.drink && this.sealed && this.straw;
  }
  addPearls() {
    this.pearls = true;
    this.pearlNode.visible = true;
  }
  fill(drinkId) {
    this.drink = drinkId;
    const c = col((RECIPES[drinkId] || SLUDGE).color);
    const g = this.glow;
    this.liquid.visible = true;
    this.liquid.tint = [c[0], c[1], c[2], 1];
    this.liquid.emissive = [c[0] * g * 0.6, c[1] * g * 0.6, c[2] * g * 0.6];
  }
  seal() {
    this.sealed = true;
    this.sealProg = 0;
    this.lid.visible = true;
  }
  addStraw() {
    this.straw = true;
    this.strawNode.visible = true;
  }
  get glow() {
    return this.drink && RECIPES[this.drink] ? RECIPES[this.drink].glow || 0 : 0;
  }
  get glowColor() {
    return this.drink ? col((RECIPES[this.drink] || SLUDGE).color) : [1, 1, 1];
  }
}

export class Straw extends Item {
  constructor() {
    super('straw');
    this.node.add(new Node(M.looseStrawMesh()));
  }
  get throwable() {
    return true;
  }
}

export class Vessel extends Item {
  constructor(app) {
    super('vessel');
    this.app = app; // 'kettle' | 'blender'
    this.contents = [];
    this.brewed = 0;
    this.over = 0;
    this.result = null; // recipe id or 'sludge'
    this.stalled = false; // brewed, but one ingredient short of a drink: waits for it
    this.burnt = false;
    this.home = null; // original station (for respawn)
    this.plan = null; // bot helpers: the recipe id it's being filled for (so several bots can share the filling)
    if (app === 'kettle') {
      this.node.add(new Node(M.kettleMesh()));
    } else {
      this.node.add(new Node(M.jugMesh()));
      this.liquidNode = new Node(M.jugLiquidMesh(), { castShadow: false });
      this.liquidNode.visible = false;
      this.node.add(this.liquidNode);
      const glass = new Node(M.jugGlassMesh(), { fx: { mode: 1 }, castShadow: false });
      glass.tint = [0.6, 0.85, 1.0, 1.0];
      this.node.add(glass);
      this.glass = glass;
    }
  }
  /** Seconds of brewing this mix needs: every ingredient brings its own share (as in Overcooked), none comes free. */
  get need() {
    return this.contents.length * (this.app === 'kettle' ? 4 : 2.9);
  }
  get done() {
    return this.contents.length > 0 && this.brewed >= this.need;
  }
  get progress() {
    return this.contents.length ? Math.min(1, this.brewed / this.need) : 0;
  }
  canAdd(ing) {
    return !this.burnt && this.result !== 'sludge' && this.contents.length < 4 && ing.ready && ing.id !== 'pearls';
  }
  get isSludge() {
    return this.burnt || this.result === 'sludge';
  }
  /** Add an ingredient. Returns 'sludge' if the mix can no longer become any drink. */
  add(ing) {
    // what's in already keeps its brewing; the new one needs its full share on top (the progress ring drops back)
    const before = this.need;
    this.contents.push(ing.id);
    this.brewed = Math.min(this.brewed, before);
    this.result = null;
    this.over = 0;
    this.stalled = false;
    let out = null;
    if (!reachableRecipes(this.app, this.contents).length) {
      // no drink can ever come out of this: it's sludge now
      this.result = 'sludge';
      out = 'sludge';
    }
    this._updateLiquid();
    return out;
  }
  /** Brewing finished: a recipe, a wait for a missing ingredient, or sludge. */
  finish() {
    const m = matchRecipe(this.app, this.contents);
    if (m) this.result = m;
    else if (reachableRecipes(this.app, this.contents).some((r) => r.missing.length)) {
      this.result = null;
      this.stalled = true;
    } else this.result = 'sludge';
    this._updateLiquid();
  }
  /** Waiting for a missing ingredient. */
  get waiting() {
    return this.stalled && !this.result;
  }
  /** Ingredients that would complete a drink (first candidate per recipe). */
  get missing() {
    return reachableRecipes(this.app, this.contents).filter((r) => r.missing.length);
  }
  reset() {
    this.plan = null;
    this.contents = [];
    this.brewed = 0;
    this.over = 0;
    this.stalled = false;
    this.result = null;
    this.burnt = false;
    this._updateLiquid();
  }
  burn() {
    this.plan = null;
    this.stalled = false;
    this.burnt = true;
    this.result = 'sludge';
    this._updateLiquid();
  }
  _updateLiquid() {
    if (!this.liquidNode) return;
    const n = this.contents.length;
    this.liquidNode.visible = n > 0;
    if (!n) return;
    let c;
    if (this.result) c = col((RECIPES[this.result] || SLUDGE).color);
    else {
      c = [0, 0, 0];
      for (const id of this.contents) {
        const k = col(INGREDIENTS[id].color);
        c[0] += k[0] / n; c[1] += k[1] / n; c[2] += k[2] / n;
      }
    }
    this.liquidNode.tint = [c[0], c[1], c[2], 1];
    const h = 0.08 + n * 0.075;
    this._liqH = h;
    this.liquidNode.scale = [1, h, 1];
    this.liquidNode.position = [0, 0.055 - 0.5 * h + h * 0.5, 0];
    this.liquidNode.position[1] = 0.055;
    const g = this.result && RECIPES[this.result] ? RECIPES[this.result].glow || 0 : 0;
    this.liquidNode.emissive = [c[0] * g * 0.6, c[1] * g * 0.6, c[2] * g * 0.6];
  }
  /** Blender running: the jug stays still and the liquid churns and bounces inside. t = null stops it. */
  slosh(t) {
    if (!this.liquidNode || !this._liqH) return;
    const L = this.liquidNode;
    if (t == null) {
      L.scale = [1, this._liqH, 1];
      L.rotation = [0, 0, 0];
      return;
    }
    const b = Math.abs(Math.sin(t * 17)) * 0.16 + Math.sin(t * 31) * 0.04;
    L.scale = [1 - b * 0.12, this._liqH * (1 + b), 1 - b * 0.12];
    L.rotation = [Math.sin(t * 23) * 0.07, t * 9, Math.cos(t * 19) * 0.07];
  }
  get glow() {
    return this.result && RECIPES[this.result] ? RECIPES[this.result].glow || 0 : 0;
  }
  get glowColor() {
    return this.result ? col((RECIPES[this.result] || SLUDGE).color) : [1, 1, 1];
  }
}

export class Extinguisher extends Item {
  constructor() {
    super('ext');
    this.node.add(new Node(M.extinguisherMesh()));
  }
}